import React, { useState } from 'react';
import axios from 'axios';
import { formatDateTimeAsStored } from '../../utils/utils';
import './ManageNotesModal.css';

const PORT = process.env.REACT_APP_PORT;

const NoteList = ({ noteData, handleNoteSelection, refreshData, acceptanceDate }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState([]);
  const [editFormData, setEditFormData] = useState({});
  const [createFormData, setCreateFormData] = useState({
    noteName: '',
    control: '',
    balance: '',
    balanceDescription: '',
    holderName: '',
    loanName: '',
    depositor: '',
    deal: '',
    accessionNumber: '',
    primaryDocument: '',
    filingDate: '',
    documentDate: '',
    closingDate: ''
  });
  const [pendingMintNotes, setPendingMintNotes] = useState([]);
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const itemsPerPage = 10;

  const handleSort = (field) => {
    const newDirection =
      sortField === field && sortDirection === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortDirection(newDirection);
  };

  const getNoteValue = (item, field) => {
    const m = item.metadata || {};
    switch (field) {
      case "loanName":
        return m.loan?.loanName || "";
      case "id":
        return parseFloat(m.id) || 0;
      case "noteName":
        return m.noteName || "";
      case "holder":
        return m.holder?.holder?.holderName || m.holder?.holderName || "";
      case "balance":
        return parseFloat(m.balance) || 0;
      case "noteTxHash":
        return m.noteTxHash || "";
      case "createdAt":
        return m.createdAt ? new Date(m.createdAt).getTime() : 0;
      default:
        return "";
    }
  };

  const arrow = (field) =>
    sortField === field ? (sortDirection === "asc" ? " ▲" : " ▼") : "";
  const thStyle = { cursor: "pointer", userSelect: "none" };

  // Filter out notes earlier than acceptanceDate when set (hide earlier notes)
  const filteredNoteData = React.useMemo(() => {
    if (!acceptanceDate) return noteData;
    const cutoff = new Date(acceptanceDate).getTime();
    return noteData.filter((item) => {
      const created = item.metadata?.createdAt;
      if (!created) return true;
      const t = new Date(created).getTime();
      return !isNaN(t) && t >= cutoff;
    });
  }, [noteData, acceptanceDate]);

  // Sortable by column header (defaults to id ascending)
  const sortedNotes = [...filteredNoteData].sort((a, b) => {
    let aVal = getNoteValue(a, sortField);
    let bVal = getNoteValue(b, sortField);
    if (typeof aVal === "string") {
      aVal = aVal.toLowerCase();
      bVal = (bVal || "").toString().toLowerCase();
    }
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  // Calculate total pages
  const totalPages = Math.ceil(sortedNotes.length / itemsPerPage);

  // Get current items
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedNotes.slice(indexOfFirstItem, indexOfLastItem);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle edit button click - opens main edit modal
  const handleEditClick = () => {
    setShowEditModal(true);
  };

  // Handle create note - opens create modal
  const handleAddNote = () => {
    // If there are existing notes, use the first one as template, otherwise start fresh
    if (sortedNotes.length > 0) {
      const firstNote = sortedNotes[0];
      setCreateFormData({
        noteName: '',
        control: firstNote.metadata.control || 'Non-Control',
        balance: '',
        balanceDescription: '',
        holderName: firstNote.metadata.holder?.holderName || firstNote.metadata.holder?.holder?.holderName || '',
        loanName: firstNote.metadata.loan?.loanName || '',
        depositor: firstNote.metadata.report?.depositor?.depositor || '',
        deal: firstNote.metadata.report?.deal?.deal || '',
        accessionNumber: firstNote.metadata.report?.accessionNumber || '',
        primaryDocument: firstNote.metadata.report?.primaryDocument || '',
        filingDate: firstNote.metadata.report?.filingDate ? new Date(firstNote.metadata.report.filingDate).toISOString().split('T')[0] : '',
        documentDate: firstNote.metadata.report?.documentDate ? new Date(firstNote.metadata.report.documentDate).toISOString().split('T')[0] : '',
        closingDate: firstNote.metadata.report?.closingDate ? new Date(firstNote.metadata.report.closingDate).toISOString().split('T')[0] : ''
      });
    } else {
      setCreateFormData({
        noteName: '',
        control: 'Non-Control',
        balance: '',
        balanceDescription: '',
        holderName: '',
        loanName: '',
        depositor: '',
        deal: '',
        accessionNumber: '',
        primaryDocument: '',
        filingDate: '',
        documentDate: '',
        closingDate: ''
      });
    }
    setShowCreateModal(true);
  };

  // Handle checkbox selection for bulk operations
  const handleCheckboxChange = (noteId) => {
    setSelectedNotes(prev => {
      if (prev.includes(noteId)) {
        return prev.filter(id => id !== noteId);
      } else {
        return [...prev, noteId];
      }
    });
  };

  // Handle select all checkbox
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allNoteIds = sortedNotes.map(note => note.metadata.id);
      setSelectedNotes(allNoteIds);
    } else {
      setSelectedNotes([]);
    }
  };

  // Check if all notes are selected
  const isAllSelected = sortedNotes.length > 0 && selectedNotes.length === sortedNotes.length;

  // Handle edit individual note - opens edit modal for specific note
  const handleEditNote = (note) => {
    setSelectedNote(note);
    // Initialize form data with current note values
    setEditFormData({
      noteName: note.metadata.noteName || '',
      control: note.metadata.control || '',
      balance: note.metadata.balance || '',
      balanceDescription: note.metadata.balanceDescription || '',
      holderName: note.metadata.holder?.holderName || note.metadata.holder?.holder?.holderName || '',
      loanName: note.metadata.loan?.loanName || '',
      depositor: note.metadata.report?.depositor?.depositor || '',
      deal: note.metadata.report?.deal?.deal || '',
      accessionNumber: note.metadata.report?.accessionNumber || '',
      primaryDocument: note.metadata.report?.primaryDocument || '',
      filingDate: note.metadata.report?.filingDate ? new Date(note.metadata.report.filingDate).toISOString().split('T')[0] : '',
      documentDate: note.metadata.report?.documentDate ? new Date(note.metadata.report.documentDate).toISOString().split('T')[0] : '',
      closingDate: note.metadata.report?.closingDate ? new Date(note.metadata.report.closingDate).toISOString().split('T')[0] : '',
    });
  };

  // Handle form input changes for edit modal
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form input changes for create modal
  const handleCreateFormChange = (e) => {
    const { name, value } = e.target;
    setCreateFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle save changes (save to EditedNote table)
  const handleSaveChanges = async () => {
    if (!selectedNote) return;

    setLoading(true);
    try {
      // Call the editNotes API to save edited data
      const response = await axios.post(`${PORT}editNotes`, {
        editedNotes: [{
          originalNoteId: selectedNote.metadata.id,
          noteName: editFormData.noteName,
          control: editFormData.control,
          balance: editFormData.balance,
          balanceDescription: editFormData.balanceDescription,
          loanName: editFormData.loanName,
          holderName: editFormData.holderName,
          depositor: editFormData.depositor,
          deal: editFormData.deal,
          accessionNumber: editFormData.accessionNumber,
          primaryDocument: editFormData.primaryDocument,
          filingDate: editFormData.filingDate,
          documentDate: editFormData.documentDate,
          closingDate: editFormData.closingDate,
        }],
        burnNoteIds: []
      });

      if (response.data.success) {
        const mintResults = response.data.data.mint;
        if (mintResults.success.length > 0) {
          const savedNote = mintResults.success[0];
          setPendingMintNotes(prev => [...prev, {
            editedNoteId: savedNote.editedNoteId,
            originalNoteId: savedNote.originalNoteId,
            noteName: editFormData.noteName
          }]);
          alert(`Note "${editFormData.noteName}" changes saved successfully! Ready for minting.`);
        } else {
          alert(`Failed to save note changes: ${mintResults.failed[0]?.error || 'Unknown error'}`);
        }
        
        // Close the edit modal
        setSelectedNote(null);
        setEditFormData({});
        
        // Refresh data if refresh function is provided
        if (refreshData) {
          refreshData();
        }
      } else {
        alert(`Error: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Error saving changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle save and mint immediately
  const handleSaveAndMint = async () => {
    if (!selectedNote) return;

    setLoading(true);
    try {
      // First save the edited note
      const saveResponse = await axios.post(`${PORT}editNotes`, {
        editedNotes: [{
          originalNoteId: selectedNote.metadata.id,
          noteName: editFormData.noteName,
          control: editFormData.control,
          balance: editFormData.balance,
          balanceDescription: editFormData.balanceDescription,
          loanName: editFormData.loanName,
          holderName: editFormData.holderName,
          depositor: editFormData.depositor,
          deal: editFormData.deal,
          accessionNumber: editFormData.accessionNumber,
          primaryDocument: editFormData.primaryDocument,
          filingDate: editFormData.filingDate,
          documentDate: editFormData.documentDate,
          closingDate: editFormData.closingDate,
        }],
        burnNoteIds: []
      });

      if (saveResponse.data.success) {
        const savedNoteId = saveResponse.data.data.mint.success[0]?.editedNoteId;
        
        if (savedNoteId) {
          // Immediately mint the saved note
          const mintResponse = await axios.post(`${PORT}mint-pending-notes`, {
            editedNoteIds: [savedNoteId]
          });

          if (mintResponse.data.success) {
            alert(`Note "${editFormData.noteName}" saved and minted successfully!`);
            setSelectedNote(null);
            setEditFormData({});
            if (refreshData) refreshData();
          } else {
            alert(`Saved but minting failed: ${mintResponse.data.message}`);
          }
        }
      } else {
        alert(`Failed to save changes: ${saveResponse.data.message}`);
      }
    } catch (error) {
      console.error('Error saving and minting:', error);
      alert('Error saving and minting. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle create note submission
  const handleCreateNote = async () => {
    if (!createFormData.noteName) {
      alert('Please enter a note name');
      return;
    }

    setLoading(true);
    try {
      // For creating a new note, we need to send it to a different endpoint
      const response = await axios.post(`${PORT}createNote`, {
        noteData: {
          noteName: createFormData.noteName,
          control: createFormData.control,
          balance: createFormData.balance,
          balanceDescription: createFormData.balanceDescription,
          holderName: createFormData.holderName,
          loanName: createFormData.loanName,
          depositor: createFormData.depositor,
          deal: createFormData.deal,
          accessionNumber: createFormData.accessionNumber,
          primaryDocument: createFormData.primaryDocument,
          filingDate: createFormData.filingDate,
          documentDate: createFormData.documentDate,
          closingDate: createFormData.closingDate,
        }
      });

      if (response.data.success) {
        alert(`Note "${createFormData.noteName}" created successfully!`);
        setShowCreateModal(false);
        setCreateFormData({
          noteName: '',
          control: 'Non-Control',
          balance: '',
          balanceDescription: '',
          holderName: '',
          loanName: '',
          depositor: '',
          deal: '',
          accessionNumber: '',
          primaryDocument: '',
          filingDate: '',
          documentDate: '',
          closingDate: ''
        });
        
        // Refresh data if refresh function is provided
        if (refreshData) {
          refreshData();
        }
      } else {
        alert(`Failed to create note: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error creating note:', error);
      alert('Error creating note. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle mint all pending notes
  const handleMintAllPending = async () => {
    if (pendingMintNotes.length === 0) {
      alert('No pending notes to mint!');
      return;
    }

    setLoading(true);
    try {
      const editedNoteIds = pendingMintNotes.map(note => note.editedNoteId);
      const response = await axios.post(`${PORT}mint-pending-notes`, {
        editedNoteIds
      });

      if (response.data.success) {
        alert(`Successfully minted ${response.data.data.success.length} notes!`);
        setPendingMintNotes([]);
        if (refreshData) refreshData();
      } else {
        alert(`Minting failed: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error minting pending notes:', error);
      alert('Error minting pending notes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete individual note
  const handleDeleteNote = async (note) => {
    if (!window.confirm(`Are you sure you want to delete note: ${note.metadata.noteName}?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${PORT}editNotes`, {
        editedNotes: [],
        burnNoteIds: [note.metadata.id]
      });

      if (response.data.success) {
        const burnResults = response.data.data.burn;
        if (burnResults.success.length > 0) {
          alert(`Note "${note.metadata.noteName}" deleted successfully!`);
          setShowEditModal(false);
          // Remove from pending mint notes if it was there
          setPendingMintNotes(prev => prev.filter(pending => pending.originalNoteId !== note.metadata.id));
          // Refresh data without page reload
          if (refreshData) {
            refreshData();
          }
        } else {
          alert(`Failed to delete note: ${burnResults.failed[0]?.error || 'Unknown error'}`);
        }
      } else {
        alert(`Error: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Error deleting note. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedNote(null);
    setSelectedNotes([]);
    setEditFormData({});
    setCreateFormData({
      noteName: '',
      control: 'Non-Control',
      balance: '',
      balanceDescription: '',
      holderName: '',
      loanName: '',
      depositor: '',
      deal: '',
      accessionNumber: '',
      primaryDocument: '',
      filingDate: '',
      documentDate: '',
      closingDate: ''
    });
  };

  return (
    <div className="note-container">
      {/* Edit Button at the top */}
      <div className="edit-controls">
        <button 
          className="edit-button"
          onClick={handleEditClick}
        >
          Edit Notes
        </button>
        {pendingMintNotes.length > 0 && (
          <button 
            className="mint-all-button"
            onClick={handleMintAllPending}
            disabled={loading}
            style={{ 
              marginLeft: '10px', 
              backgroundColor: '#28a745', 
              color: 'white',
              padding: '8px 16px',
              borderRadius: '4px',
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Minting...' : `Mint All Pending (${pendingMintNotes.length})`}
          </button>
        )}
      </div>

      <table className="note-table">
        <thead>
          <tr>
            <th onClick={() => handleSort("loanName")} style={thStyle}>Loan Name{arrow("loanName")}</th>
            <th onClick={() => handleSort("id")} style={thStyle}>Note Id{arrow("id")}</th>
            <th onClick={() => handleSort("noteName")} style={thStyle}>Note Name{arrow("noteName")}</th>
            <th onClick={() => handleSort("holder")} style={thStyle}>Parent Holder{arrow("holder")}</th>
            <th onClick={() => handleSort("balance")} style={thStyle}>Balance{arrow("balance")}</th>
            <th onClick={() => handleSort("noteTxHash")} style={thStyle}>Transaction Hash{arrow("noteTxHash")}</th>
            <th onClick={() => handleSort("createdAt")} style={thStyle}>Created{arrow("createdAt")}</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.length > 0 ? (
            currentItems.map((note, index) => (
              <tr key={index} onClick={() => handleNoteSelection(note)}>
                <td>{note.metadata.loan?.loanName || 'N/A'}</td>
                <td>{note.metadata.id}</td>
                <td>{note.metadata.noteName}</td>
                <td>
                  {note.metadata.holder?.holder?.holderName || note.metadata.holder?.holderName || 'N/A'}
                </td>
                <td>{note.metadata.balance ?? 'N/A'}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  {note.metadata.noteTxHash ? (
                    <span
                      className="tx-hash"
                      title={note.metadata.noteTxHash}
                      onClick={() => window.open(`${process.env.REACT_APP_EXP_URL}${note.metadata.noteTxHash}`, '_blank')}
                    >
                      {note.metadata.noteTxHash.slice(0, 8)}…{note.metadata.noteTxHash.slice(-8)}
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>{formatDateTimeAsStored(note.metadata.createdAt)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7">No notes available</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Main Edit Notes Modal (list view) */}
      {showEditModal && !showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '1200px', width: '95%' }}>
            <button
              className="close-button"
              onClick={() => {
                setShowEditModal(false);
                resetForm();
              }}
            >
              ×
            </button>
            <h3>Manage Notes</h3>

            {/* Stats and Info Section */}
            <div className="mn-stats">
              <div><strong>Total Notes:</strong> {sortedNotes.length}</div>
              <div><strong>Selected:</strong> {selectedNotes.length}</div>
              <div><strong>Minted Notes:</strong> {sortedNotes.filter(note => note.metadata.noteTxHash).length}</div>
              <div><strong>Pending Mint:</strong> {pendingMintNotes.length}</div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  className="btn btn-accent"
                  onClick={handleAddNote}
                >
                  <i className="bi bi-plus-lg" /> Create new note
                </button>
                {pendingMintNotes.length > 0 && (
                  <button
                    className="btn"
                    onClick={handleMintAllPending}
                    disabled={loading}
                    style={{ background: 'var(--success)', color: 'var(--text-on-dark)' }}
                  >
                    {loading ? 'Minting...' : `Mint pending (${pendingMintNotes.length})`}
                  </button>
                )}
              </div>
            </div>

            {/* Pending Mint Notes Section */}
            {pendingMintNotes.length > 0 && (
              <div className="mn-pending-banner">
                <h4>Pending minting</h4>
                <div className="mn-pending-chips">
                  {pendingMintNotes.map((pendingNote, index) => (
                    <span key={index} className="mn-pending-chip">
                      {pendingNote.noteName}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="notes-list">
              <h4 className="mn-section-title">Existing notes</h4>
              {sortedNotes.length > 0 ? (
                <div className="edit-notes-container">
                  <div className="mn-toolbar">
                    <div>
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        id="select-all-notes"
                      />
                      <label htmlFor="select-all-notes">Select all</label>
                    </div>
                    <div className="mn-count">{selectedNotes.length} notes selected</div>
                  </div>

                  <div className="mn-list">
                    {sortedNotes.map((note, index) => {
                      const isPending = pendingMintNotes.some(p => p.originalNoteId === note.metadata.id);
                      const isSelected = selectedNotes.includes(note.metadata.id);
                      const itemClass = ['mn-item',
                        isPending ? 'mn-item-pending' : '',
                        isSelected ? 'mn-item-selected' : '',
                      ].filter(Boolean).join(' ');
                      return (
                        <div key={index} className={itemClass}>
                          <div className="mn-item-row">
                            <div className="mn-item-main">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleCheckboxChange(note.metadata.id)}
                              />
                              <div className="mn-item-body">
                                <div className="mn-item-top">
                                  <div>
                                    <strong>Note name:</strong> {note.metadata.noteName}
                                    {isPending && <span className="mn-pending-badge">Pending mint</span>}
                                  </div>
                                  <div><strong>ID:</strong> {note.metadata.id}</div>
                                  <div><strong>Loan:</strong> {note.metadata.loan?.loanName || 'N/A'}</div>
                                  <div>
                                    <strong>Balance:</strong>{' '}
                                    {note.metadata.balance ? `$${Number(note.metadata.balance).toLocaleString()}` : 'N/A'}
                                  </div>
                                </div>

                                <div className="mn-item-bottom">
                                  <div>
                                    <strong>Status:</strong>
                                    <span className={note.metadata.noteTxHash ? 'mn-status-minted' : 'mn-status-pending'}>
                                      {note.metadata.noteTxHash ? 'Minted' : 'Not minted'}
                                    </span>
                                  </div>
                                  <div>
                                    <strong>Created:</strong> {formatDateTimeAsStored(note.metadata.createdAt)}
                                  </div>
                                  {note.metadata.noteTxHash && (
                                    <div>
                                      <strong>Tx hash:</strong>{' '}
                                      <span
                                        className="tx-hash"
                                        style={{ marginLeft: 5 }}
                                        title={note.metadata.noteTxHash}
                                        onClick={() => window.open(`${process.env.REACT_APP_EXP_URL}${note.metadata.noteTxHash}`, '_blank')}
                                      >
                                        {note.metadata.noteTxHash.slice(0, 8)}…{note.metadata.noteTxHash.slice(-8)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="mn-item-actions">
                              <button
                                className="action-button edit-btn"
                                onClick={() => handleEditNote(note)}
                                disabled={loading}
                              >
                                {isPending ? 'Edit pending' : 'Edit & mint'}
                              </button>
                              <button
                                className="action-button delete-btn"
                                onClick={() => handleDeleteNote(note)}
                                disabled={loading}
                                style={{ background: 'var(--danger)' }}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="muted">No notes available to edit</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Note Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <button
              className="close-button"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
            >
              ×
            </button>
            <h3>Create New Note</h3>
            
            <div className="note-form" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '20px' }}>
              {/* Note Information */}
              <div className="form-group">
                <label>Note Name: *</label>
                <input 
                  type="text" 
                  name="noteName"
                  value={createFormData.noteName || ''}
                  onChange={handleCreateFormChange}
                  className="form-input"
                  placeholder="Enter note name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Control:</label>
                <select 
                  name="control"
                  value={createFormData.control || 'Non-Control'}
                  onChange={handleCreateFormChange}
                  className="form-select"
                >
                  <option value="Control">Control</option>
                  <option value="Non-Control">Non-Control</option>
                </select>
              </div>

              <div className="form-group">
                <label>Balance:</label><br/>
                <input 
                  type="number" 
                  name="balance"
                  value={createFormData.balance || ''}
                  onChange={handleCreateFormChange}
                  className="form-input"
                  step="0.01"
                  placeholder="Enter balance amount"
                />
              </div>

              <div className="form-group">
                <label>Balance Description:</label>
                <input 
                  type="text" 
                  name="balanceDescription"
                  value={createFormData.balanceDescription || ''}
                  onChange={handleCreateFormChange}
                  className="form-input"
                  placeholder="Enter balance description"
                />
              </div>
            </div>

            <div className="form-actions" style={{ gridColumn: '1 / -1', display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button 
                className="action-button cancel-button"
                onClick={() => setShowCreateModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                className="action-button add-button"
                onClick={handleCreateNote}
                disabled={loading || !createFormData.noteName}
                style={{ backgroundColor: '#152c70', color: 'white' }}
              >
                {loading ? 'Creating...' : 'Create Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Individual Note Edit Modal */}
      {selectedNote && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
            <button
              className="close-button"
              onClick={() => {
                setSelectedNote(null);
                setEditFormData({});
              }}
            >
              ×
            </button>
            <h3>Edit &amp; Mint Note: {selectedNote.metadata.noteName}</h3>

            <div className="mn-hero">
              <div className="mn-hero-grid">
                <div><strong>Note ID:</strong> {selectedNote.metadata.id}</div>
                <div>
                  <strong>Current status:</strong>
                  <span className={selectedNote.metadata.noteTxHash ? 'mn-status-minted' : 'mn-status-pending'}>
                    {selectedNote.metadata.noteTxHash ? 'Minted' : 'Not minted'}
                  </span>
                </div>
                <div><strong>Loan:</strong> {selectedNote.metadata.loan?.loanName || 'N/A'}</div>
                <div><strong>Holder:</strong> {selectedNote.metadata.holder?.holderName || selectedNote.metadata.holder?.holder?.holderName || 'N/A'}</div>
              </div>
            </div>
            
            <div className="note-form">
              <div className="form-group">
                <label htmlFor="em-noteName">Note name</label>
                <input
                  id="em-noteName"
                  type="text"
                  name="noteName"
                  value={editFormData.noteName || ''}
                  onChange={handleEditFormChange}
                  className="form-input"
                  placeholder="e.g. Class A-1"
                />
              </div>

              <div className="form-group">
                <label htmlFor="em-control">Control</label>
                <select
                  id="em-control"
                  name="control"
                  value={editFormData.control || ''}
                  onChange={handleEditFormChange}
                  className="form-select"
                >
                  <option value="">Select control</option>
                  <option value="Control">Control</option>
                  <option value="Non-Control">Non-Control</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="em-balance">Balance</label>
                <input
                  id="em-balance"
                  type="number"
                  name="balance"
                  value={editFormData.balance || ''}
                  onChange={handleEditFormChange}
                  className="form-input"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label htmlFor="em-balanceDesc">Balance description</label>
                <input
                  id="em-balanceDesc"
                  type="text"
                  name="balanceDescription"
                  value={editFormData.balanceDescription || ''}
                  onChange={handleEditFormChange}
                  className="form-input"
                  placeholder="Optional note about this balance"
                />
              </div>
            </div>

            <div className="mn-form-actions">
              <button
                type="button"
                className="btn btn-ghost-themed"
                onClick={() => {
                  setSelectedNote(null);
                  setEditFormData({});
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                onClick={handleSaveChanges}
                disabled={loading}
              >
                {loading ? (<><i className="bi bi-arrow-repeat" style={{ animation: 'spin 0.7s linear infinite' }} /> Saving…</>) : (<><i className="bi bi-save" /> Save changes</>)}
              </button>
              <button
                type="button"
                className="btn btn-mint"
                onClick={handleSaveAndMint}
                disabled={loading}
              >
                {loading ? (<><i className="bi bi-arrow-repeat" style={{ animation: 'spin 0.7s linear infinite' }} /> Minting…</>) : (<><i className="bi bi-fire" /> Save &amp; mint now</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pagination">
        <button
          className="arrow-button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &larr;
        </button>

        {(() => {
          const pages = [];
          let startPage = Math.max(1, currentPage - 2);
          let endPage = Math.min(totalPages, startPage + 4);

          // Adjust start page if we're near the end
          if (endPage - startPage < 4) {
            startPage = Math.max(1, endPage - 4);
          }

          for (let i = startPage; i <= endPage; i++) {
            pages.push(
              <button
                key={i}
                onClick={() => handlePageChange(i)}
                className={`page-button ${currentPage === i ? "active" : ""}`}
              >
                {i}
              </button>
            );
          }

          return pages;
        })()}

        <button
          className="arrow-button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          &rarr;
        </button>
      </div>
    </div>
  );
};

export default NoteList;